import _ from "lodash";

// Configuration for each Certification Specification (CS) type, including which
// pilot rating category (Aeroplane vs Helicopter) and competency/hours keys to use.
export const CS_TYPES = ["CS-23", "CS-23P", "CS-25", "CS-27", "CS-29"];

export const CS_CONFIGS = {
    "CS-23": {
        competencyKey: "CS-23",
        hoursKey: "CS-23",
        overrideType: "CS23",
        categoryKey: "AeroplaneCategory",
        vehicle: "aeroplane",
        special: true
    },
    "CS-23P": {
        competencyKey: "CS-23P",
        hoursKey: "CS-23P",
        overrideType: "CS23P",
        categoryKey: "AeroplaneCategory",
        vehicle: "aeroplane"
    },
    "CS-25": {
        competencyKey: "CS-25",
        hoursKey: "CS-25",
        overrideType: "CS-25",
        categoryKey: "AeroplaneCategory",
        vehicle: "aeroplane"
    },
    "CS-27": {
        competencyKey: "CS-27",
        hoursKey: "CS-27",
        overrideType: "CS-27",
        categoryKey: "HelicopterCategory",
        vehicle: "helicopter"
    },
    "CS-29": {
        competencyKey: "CS-29",
        hoursKey: "CS-29",
        overrideType: "CS-29",
        categoryKey: "HelicopterCategory",
        vehicle: "helicopter"
    }
};

function getOveride(override, type, cat) {
    // If override is an empty object, there is no override. Otherwise, if the
    // override for this type/category is explicitly "No Override" return false,
    // else return the override value suffixed with " (Override)".
    if (_.isEmpty(override)) {
        return false;
    }
    
    return override && override[`${cat}${type.replace("-", "")}`] === "No Override"
        ? false
        : (_.get(override, `${cat}${type.replace("-", "")}`, false)) + " (Override)";
}

function getCategoryFlags(item, categoryKey) {
    const category = item?.[categoryKey];
    return {
        hasCatOne: category === "Category1",
        hasCatTwo: category === "Category2"
    };
}

function getCatFourPermission(item, csKey) {
    const csHours = item?.FlightHours?.[csKey];
    const totalHours = item?.FlightHours?.Total;

    if (csHours?.Total > 1000 && csHours?.TotalLast > 50) return "PiC";
    if (totalHours > 1000 && csHours?.Total > 50) return "CoP";

    return "Not Applicable";
}

// Computes the structured Cat 1 / Cat 2 / Cat 4 permissions for a given CS type.
// Returns an object: { cat1, cat2, cat4 } where each value is one of
// "PiC", "CoP", "CoP*", "Not Applicable", or an override string ("... (Override)").
function computePermissions(item, config) {
    if (item.Role !== "Pilot") {
        return { cat1: "Not Applicable", cat2: "Not Applicable", cat4: "Not Applicable" };
    }

    const competencyLevel = item?.Competency?.[config.competencyKey];
    const { hasCatOne, hasCatTwo } = getCategoryFlags(item, config.categoryKey);
    const isCompetencyOneOrTwo = competencyLevel === 1 || competencyLevel === 2;

    const cat1Override = getOveride(item?.raw?.Override || {}, config.overrideType, "CatOne");
    const cat2Override = getOveride(item?.raw?.Override || {}, config.overrideType, "CatTwo");
    const cat4Override = getOveride(item?.raw?.Override || {}, config.overrideType, "CatFour");
    const cat4PermissionFromHours = getCatFourPermission(item, config.hoursKey);

    const cat1 = cat1Override || config.resolveCat1({
        competencyLevel,
        hasCatOne,
        hasCatTwo,
        isCompetencyOneOrTwo
    });

    const cat2 = cat2Override || config.resolveCat2({
        competencyLevel,
        hasCatOne,
        hasCatTwo,
        isCompetencyOneOrTwo
    });

    const cat4 = cat4Override || config.resolveCat4({
        competencyLevel,
        cat4PermissionFromHours
    });

    return { cat1, cat2, cat4 };
}

function computeCS23Permissions(item) {
    return computePermissions(item, {
        ...CS_CONFIGS["CS-23"],
        resolveCat1({ hasCatOne, hasCatTwo, isCompetencyOneOrTwo }) {
            if (isCompetencyOneOrTwo && hasCatOne) return "PiC";
            if (isCompetencyOneOrTwo && (hasCatOne || hasCatTwo)) return "CoP";
            return "Not Applicable";
        },
        resolveCat2({ competencyLevel, hasCatOne, hasCatTwo, isCompetencyOneOrTwo }) {
            if (isCompetencyOneOrTwo && (hasCatOne || hasCatTwo)) return "PiC";
            if (competencyLevel === 4) return "CoP*";
            return "Not Applicable";
        },
        resolveCat4({ competencyLevel, cat4PermissionFromHours }) {
            console.log("Computing Cat 4 permission with competencyLevel:", competencyLevel, "and cat4PermissionFromHours:", cat4PermissionFromHours);
            if (competencyLevel >= 1) return cat4PermissionFromHours;
            return "Not Applicable";
        }
    });
}

function computeStandardPermissions(item, config) {
    return computePermissions(item, {
        ...config,
        resolveCat1({ competencyLevel, hasCatOne, isCompetencyOneOrTwo }) {
            if (competencyLevel === 1 && hasCatOne) return "PiC";
            if (isCompetencyOneOrTwo && hasCatOne) return "CoP";
            return "Not Applicable";
        },
        resolveCat2({ competencyLevel, hasCatOne, hasCatTwo, isCompetencyOneOrTwo }) {
            if (isCompetencyOneOrTwo && hasCatOne) return "PiC";
            if (isCompetencyOneOrTwo && (hasCatOne || hasCatTwo)) return "CoP";
            if (competencyLevel === 4) return "CoP*";
            return "Not Applicable";
        },
        resolveCat4({ competencyLevel, cat4PermissionFromHours }) {
            if (competencyLevel >= 1) return cat4PermissionFromHours;
            return "Not Applicable";
        }
    });
}

// Returns structured { cat1, cat2, cat4 } permissions for a crew member and a CS type.
export function getPermissionsForType(item, csType) {
    const config = CS_CONFIGS[csType];
    if (!config) return { cat1: "Not Applicable", cat2: "Not Applicable", cat4: "Not Applicable" };
    if (config.special) return computeCS23Permissions(item);
    return computeStandardPermissions(item, config);
}

// Returns the single best-case permission label for a CS type (e.g. "Cat 1 PiC"),
// or null if the crew member holds no permission for that CS type.
// Ranking: Cat 1 PiC > Cat 1 CoP > Cat 2 PiC > Cat 2 CoP/CoP* > Cat 4 PiC > Cat 4 CoP.
export function bestPermission(perms) {
    const ordered = [
        { cat: 1, value: perms.cat1 },
        { cat: 2, value: perms.cat2 },
        { cat: 4, value: perms.cat4 }
    ];

    let best = null;
    let bestScore = -1;

    for (const { cat, value } of ordered) {
        if (!value || value === "Not Applicable") continue;

        const v = value.toString();
        const permRank = v.includes("PiC") ? 2 : v.includes("CoP") ? 1 : 0;
        const catWeight = cat === 1 ? 100 : cat === 2 ? 50 : 10;
        const score = catWeight + permRank;

        if (score > bestScore) {
            bestScore = score;
            best = `Cat ${cat} ${v}`;
        }
    }

    return best;
}

// Returns the best-case permission label for a crew member and CS type directly.
export function bestPermissionForType(item, csType) {
    if (item?.Role !== "Pilot") return null;
    return bestPermission(getPermissionsForType(item, csType));
}
